
import { Component, Vue } from 'vue-property-decorator'
import { listStoreVersions, updateStoreVersion, IStoreVersion } from '@/api/storeVersion'
import { cloneDeep } from 'lodash'
import { Form } from 'element-ui'

const defaultVersionData: IStoreVersion = {
  version: 0,
  isMajor: false,
  storeUrl: '',
  os: 0
}

enum OSType {
  UI = 0,
  IOS = 1,
  Android = 2
}

@Component({
  name: 'StoreVersionList'
})
export default class extends Vue {
  private list: IStoreVersion[] = []
  private listLoading = true
  private dialogFormVisible = false
  private tempVersionData: IStoreVersion = cloneDeep(defaultVersionData)

  private rules = {
    version: [
      { required: true, message: 'Version is required', trigger: 'blur' }
    ],
    storeUrl: [
      { required: true, message: 'Store URL is required', trigger: 'blur' },
      { type: 'url', message: 'Please enter a valid URL', trigger: 'blur' }
    ]
  }

  get filteredList() {
    // Sort by OS type (iOS, Android, then UI) and version
    return this.list.slice().sort((a, b) => {
      if (a.os !== b.os) {
        return (a.os || 0) - (b.os || 0)
      }
      return (b.version || 0) - (a.version || 0)
    })
  }

  created() {
    this.fetchData()
  }

  private getOSName(os?: number): string {
    switch (os) {
      case OSType.IOS:
        return 'iOS'
      case OSType.Android:
        return 'Android'
      case OSType.UI:
        return 'UI'
      default:
        return 'Unknown'
    }
  }

  private getOSTagType(os?: number): string {
    switch (os) {
      case OSType.IOS:
        return 'primary'
      case OSType.Android:
        return 'success'
      case OSType.UI:
        return 'info'
      default:
        return 'warning'
    }
  }

  private async fetchData() {
    this.listLoading = true
    try {
      const response: any = await listStoreVersions()
      if (response.data && response.data.data) {
        this.list = response.data.data
      } else {
        this.list = []
      }
    } catch (error) {
      console.error('Error fetching store versions:', error)
      this.$notify({
        title: 'Error',
        message: 'Failed to load store version information',
        type: 'error',
        duration: 2000
      })
      this.list = []
    } finally {
      this.listLoading = false
    }
  }

  private handleUpdate(row: IStoreVersion) {
    this.tempVersionData = Object.assign({}, row)
    this.dialogFormVisible = true
    this.$nextTick(() => {
      (this.$refs.dataForm as Form).clearValidate()
    })
  }

  private updateData() {
    const form = this.$refs.dataForm as Form
    form.validate(async(valid) => {
      if (valid) {
        try {
          const response: any = await updateStoreVersion(
            this.tempVersionData.id!,
            {
              version: this.tempVersionData.version,
              isMajor: this.tempVersionData.isMajor,
              storeUrl: this.tempVersionData.storeUrl
            }
          )

          if (response.data && response.data.status && response.data.status.code === 'success') {
            const index = this.list.findIndex((v: IStoreVersion) => v.id === response.data.data.id)
            if (index !== -1) {
              this.list.splice(index, 1, response.data.data)
            }
            this.dialogFormVisible = false
            this.$notify({
              title: 'Success',
              message: 'Store version updated successfully',
              type: 'success',
              duration: 2000
            })
          }
        } catch (error: any) {
          this.$notify({
            title: 'Error',
            message: error.response?.data?.status?.message || 'Failed to update store version',
            type: 'error',
            duration: 2000
          })
        }
      }
    })
  }
}
